import {
  COST_AREA_OPTIONS,
  COST_BEHAVIOR_OPTIONS,
  COST_TRACEABILITY_OPTIONS,
  getOptionLabel,
} from '../constants/supplierLedgerConstants';
import { VAT_RATE_OPTIONS } from '../constants/supplierLedgerUiConstants';
import { formatAmount, parseNumber, toISODate } from './supplierLedgerFormatters';

const emptyInvoiceForm = () => ({
  uuid_legal_entity: '',
  invoice_date: toISODate(),
  due_date: '',
  receipt_type: 'A',
  receipt_number: '',
  concept: '',
  accounting_account_label: '',
  cost_behavior: 'fixed',
  cost_traceability: 'direct',
  cost_area: 'work',
  currency: 'ARS',
  exchange_rate_to_ars: '',
  net_amount: '',
  vat_amount: '',
  vat_perception_amount: '0',
  gross_income_perception_amount: '0',
  total_amount: '',
  duplicate_override_reason: '',
});
const getGeneratedPaymentOrderNumbers = (invoice) => {
  if (!invoice) return [];
  if (Array.isArray(invoice.pending_payment_order_numbers)) {
    return invoice.pending_payment_order_numbers.filter(Boolean);
  }
  if (Array.isArray(invoice.pending_payment_orders)) {
    return invoice.pending_payment_orders
      .map((order) => order.order_number)
      .filter(Boolean);
  }
  if (typeof invoice.has_pending_payment_order === 'boolean') {
    return [];
  }
  if (Array.isArray(invoice.active_payment_order_numbers)) {
    return invoice.active_payment_order_numbers.filter(Boolean);
  }
  if (Array.isArray(invoice.active_payment_orders)) {
    return invoice.active_payment_orders
      .map((order) => order.order_number)
      .filter(Boolean);
  }
  return [];
};

const hasGeneratedPaymentOrder = (invoice) => {
  if (!invoice) return false;
  if (typeof invoice.has_pending_payment_order === 'boolean') {
    return invoice.has_pending_payment_order;
  }
  return (
    Boolean(invoice.has_active_payment_order)
    || getGeneratedPaymentOrderNumbers(invoice).length > 0
    || (
      ['open', 'partially_paid'].includes(invoice.status)
      && Number(invoice.committed_amount || 0) > 0
    )
  );
};

const getInvoiceDisplayStatus = (invoice) => (
  ['open', 'partially_paid'].includes(invoice?.status) && hasGeneratedPaymentOrder(invoice)
    ? 'payment_order_generated'
    : invoice?.status
);

const isInvoiceSelectableForPaymentOrder = (invoice) => (
  ['open', 'partially_paid'].includes(invoice?.status) && !hasGeneratedPaymentOrder(invoice)
);

const getGeneratedPaymentOrderTooltip = (invoice) => {
  const numbers = getGeneratedPaymentOrderNumbers(invoice);
  if (!numbers.length) return 'La factura ya tiene una orden de pago generada';
  return `OP generada: ${numbers.map((number) => `#${number}`).join(', ')}`;
};

const reportAmountFields = [
  'total_invoiced_ars',
  'paid_amount_ars',
  'pending_amount_ars',
  'ledger_balance_ars',
  'total_amount_ars',
  'balance_ars',
];

const hasReportAmount = (row) => reportAmountFields.some((field) => (
  Math.abs(Number(row?.[field] || 0)) > 0
));

const reportClassificationOptions = {
  cost_behavior: COST_BEHAVIOR_OPTIONS,
  cost_traceability: COST_TRACEABILITY_OPTIONS,
  cost_area: COST_AREA_OPTIONS,
};

const isClassificationField = (field) => Boolean(reportClassificationOptions[field]);

const getClassificationFieldLabel = (field, value) => (
  getOptionLabel(reportClassificationOptions[field] || [], value) || 'Sin clasificar'
);

const getClassificationSummaryLabel = (row) => [
  getClassificationFieldLabel('cost_behavior', row.cost_behavior),
  getClassificationFieldLabel('cost_traceability', row.cost_traceability),
  getClassificationFieldLabel('cost_area', row.cost_area),
].join(' / ');

const formatCompactAmount = (value) => {
  const number = Number(value || 0);
  const abs = Math.abs(number);
  if (abs >= 1000000) return `$ ${(number / 1000000).toLocaleString('es-AR', { maximumFractionDigits: 1 })} M`;
  if (abs >= 1000) return `$ ${(number / 1000).toLocaleString('es-AR', { maximumFractionDigits: 0 })} k`;
  return formatAmount(number);
};

const monthLabels = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const getInvoiceMonthKey = (invoice) => {
  const rawDate = invoice?.invoice_date;
  if (!rawDate) return null;
  const date = new Date(`${String(rawDate).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return {
    key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
    label: `${monthLabels[date.getMonth()]} ${String(date.getFullYear()).slice(2)}`,
  };
};
const createAmountLine = () => ({
  id: `${Date.now()}-${Math.random()}`,
  gross_amount: '',
  vat_rate: '21',
});

const createEmptyPaymentForm = (emissionDate = toISODate()) => ({
  pay_now: false,
  payment_method: 'transfer',
  fund_account_id: '',
  uuid_bank_account: '',
  uuid_cash_account: '',
  uuid_movement_type: '',
  voucher_number: '',
  due_date: emissionDate,
  retentions: [],
  checks: [],
});

const createRetentionLine = () => ({
  id: `${Date.now()}-${Math.random()}`,
  retention_type: 'income_tax',
  certificate_number: '',
  amount: '',
  observations: '',
});

const createCheckLine = (currency = 'ARS', emissionDate = toISODate()) => ({
  id: `${Date.now()}-${Math.random()}`,
  check_kind: 'own',
  uuid_bank_account: '',
  uuid_exchange_check_movement: '',
  check_number: '',
  emission_date: emissionDate,
  due_date: emissionDate,
  currency,
  exchange_rate_to_ars: '',
  amount: '',
});

const amountToInput = (value, fallback = '', maximumFractionDigits = 2) => {
  if (value === null || value === undefined || value === '') return fallback;
  const number = Number(value);
  if (Number.isNaN(number)) return fallback;
  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
    maximumFractionDigits,
  }).format(number);
};

const dateToInput = (value) => (value ? String(value).slice(0, 10) : '');

const normalizeVatRate = (value) => {
  const rate = Number(value || 0);
  const exact = VAT_RATE_OPTIONS.find((option) => Number(option.value) === rate);
  if (exact) return exact.value;

  return VAT_RATE_OPTIONS.reduce((closest, option) => (
    Math.abs(Number(option.value) - rate) < Math.abs(Number(closest.value) - rate) ? option : closest
  ), VAT_RATE_OPTIONS[0]).value;
};

const buildAmountLinesFromInvoice = (invoice) => {
  const taxLines = Array.isArray(invoice?.tax_lines) ? invoice.tax_lines : [];
  const lines = taxLines
    .filter((line) => Number(line.taxable_base_amount || 0) > 0 || Number(line.amount || 0) > 0)
    .map((line) => {
      const net = Number(line.taxable_base_amount || 0);
      const vat = Number(line.amount || 0);
      return {
        id: `${Date.now()}-${Math.random()}`,
        gross_amount: amountToInput(net + vat),
        vat_rate: normalizeVatRate(line.rate),
      };
    });

  if (lines.length) return lines;

  const perceptions = Number(invoice?.vat_perception_amount || 0) + Number(invoice?.gross_income_perception_amount || 0);
  const gross = Math.max(Number(invoice?.total_amount || 0) - perceptions, 0);
  const net = Number(invoice?.net_amount || 0);
  const vat = Number(invoice?.vat_amount || 0);
  const inferredRate = net > 0 ? (vat / net) * 100 : 21;

  return [{
    id: `${Date.now()}-${Math.random()}`,
    gross_amount: gross > 0 ? amountToInput(gross) : '',
    vat_rate: normalizeVatRate(inferredRate),
  }];
};

const buildInvoiceFormFromInvoice = (invoice) => ({
  ...emptyInvoiceForm(),
  uuid_legal_entity: invoice?.uuid_legal_entity || '',
  invoice_date: dateToInput(invoice?.invoice_date) || toISODate(),
  due_date: dateToInput(invoice?.due_date),
  receipt_type: invoice?.receipt_type || 'A',
  receipt_number: invoice?.receipt_number || '',
  concept: invoice?.concept || '',
  accounting_account_label: invoice?.accounting_account_label || '',
  cost_behavior: invoice?.cost_behavior || 'fixed',
  cost_traceability: invoice?.cost_traceability || 'direct',
  cost_area: invoice?.cost_area || 'work',
  currency: invoice?.currency || 'ARS',
  exchange_rate_to_ars: amountToInput(invoice?.exchange_rate_to_ars, '', 6),
  net_amount: amountToInput(invoice?.net_amount),
  vat_amount: amountToInput(invoice?.vat_amount),
  vat_perception_amount: amountToInput(invoice?.vat_perception_amount, '0'),
  gross_income_perception_amount: amountToInput(invoice?.gross_income_perception_amount, '0'),
  total_amount: amountToInput(invoice?.total_amount),
  duplicate_override_reason: invoice?.duplicate_override_reason || '',
});

const parseVatRate = (value) => {
  const parsed = Number(String(value || '0').replace(',', '.'));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const calculateAmountLine = (line) => {
  const gross = parseNumber(line.gross_amount);
  const vatRate = parseVatRate(line.vat_rate);
  const divisor = 1 + (vatRate / 100);
  const net = divisor > 0 ? gross / divisor : gross;
  const vat = gross - net;

  return {
    gross,
    vatRate,
    net,
    vat,
  };
};
const buildPaymentOrderGroups = (invoices, providerMap = {}) => {
  const groups = new Map();

  invoices.forEach((invoice) => {
    const providerId = invoice.uuid_legal_entity || 'sin-proveedor';
    const currency = invoice.currency || 'ARS';
    const exchangeRate = invoice.exchange_rate_to_ars || '';
    const key = `${providerId}|${currency}|${exchangeRate}`;

    if (!groups.has(key)) {
      groups.set(key, {
        id: key,
        providerId,
        providerName: providerMap[providerId] || invoice.provider_name || 'Proveedor sin nombre',
        currency,
        exchangeRateToArs: exchangeRate || null,
        invoices: [],
      });
    }

    groups.get(key).invoices.push(invoice);
  });

  return Array.from(groups.values()).map((group) => ({
    ...group,
    pendingTotal: group.invoices.reduce((sum, invoice) => sum + Number(invoice.pending_amount || 0), 0),
    pendingTotalArs: group.invoices.reduce((sum, invoice) => sum + Number(invoice.pending_amount_ars || 0), 0),
  }));
};

const buildPaymentOrderProviderGroups = (invoices, providerMap = {}) => {
  const groups = new Map();

  invoices.forEach((invoice) => {
    const providerId = invoice.uuid_legal_entity || 'sin-proveedor';

    if (!groups.has(providerId)) {
      groups.set(providerId, {
        id: providerId,
        providerId,
        providerName: providerMap[providerId] || invoice.provider_name || 'Proveedor sin nombre',
        invoices: [],
      });
    }

    groups.get(providerId).invoices.push(invoice);
  });

  return Array.from(groups.values()).map((group) => ({
    ...group,
    currencies: Array.from(new Set(group.invoices.map((invoice) => invoice.currency || 'ARS'))),
    pendingTotalArs: group.invoices.reduce((sum, invoice) => sum + Number(invoice.pending_amount_ars || 0), 0),
  }));
};

const getPaymentOrderStatusLabel = (status) => {
  switch (status) {
    case 'processing':
      return 'En proceso';
    case 'ready':
      return 'Listo';
    case 'done':
      return 'Generada';
    case 'error':
      return 'Error';
    default:
      return 'Pendiente';
  }
};

const getPaymentOrderStatusColor = (status) => {
  switch (status) {
    case 'processing':
      return 'warning';
    case 'ready':
      return 'info';
    case 'done':
      return 'success';
    case 'error':
      return 'error';
    default:
      return 'default';
  }
};
const emptyAdjustmentForm = () => ({
  uuid_legal_entity: '',
  adjustment_type: 'opening_balance',
  adjustment_date: toISODate(),
  concept: '',
  currency: 'ARS',
  exchange_rate_to_ars: '',
  amount: '',
});
const truncateChartLabel = (value, maxLength = 20) => {
  const label = String(value || '-');
  return label.length > maxLength ? `${label.slice(0, maxLength - 1)}…` : label;
};

export {
  emptyInvoiceForm,
  getGeneratedPaymentOrderNumbers,
  hasGeneratedPaymentOrder,
  getInvoiceDisplayStatus,
  isInvoiceSelectableForPaymentOrder,
  getGeneratedPaymentOrderTooltip,
  hasReportAmount,
  isClassificationField,
  getClassificationFieldLabel,
  getClassificationSummaryLabel,
  formatCompactAmount,
  getInvoiceMonthKey,
  createAmountLine,
  createEmptyPaymentForm,
  createRetentionLine,
  createCheckLine,
  amountToInput,
  dateToInput,
  normalizeVatRate,
  buildAmountLinesFromInvoice,
  buildInvoiceFormFromInvoice,
  parseVatRate,
  calculateAmountLine,
  buildPaymentOrderGroups,
  buildPaymentOrderProviderGroups,
  getPaymentOrderStatusLabel,
  getPaymentOrderStatusColor,
  emptyAdjustmentForm,
  truncateChartLabel,
};
